/**
 * Países, para el perfil de cada jugador.
 *
 * Se guarda el código ISO 3166-1 alfa-2 («VE», «ES», «MX»), no el nombre ni
 * el emoji: el nombre se traduce solo con `Intl.DisplayNames` y la bandera
 * se compone a partir del propio código, así que no hay que mantener listas
 * de nombres ni imágenes de banderas.
 */

/** Los 249 códigos oficiales de ISO 3166-1 alfa-2. */
export const COUNTRY_CODES: string[] = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW',
  'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET',
  'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT',
  'GU', 'GW', 'GY',
  'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
  'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS',
  'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
  'OM',
  'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY',
  'QA',
  'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'UM', 'US', 'UY', 'UZ',
  'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU',
  'WF', 'WS',
  'YE', 'YT',
  'ZA', 'ZM', 'ZW',
];

/**
 * Países que se ofrecen los primeros en el desplegable.
 *
 * La aplicación está en español y su gente viene sobre todo de aquí; el
 * resto de la lista sigue estando, solo que más abajo.
 */
export const PRIORITY_COUNTRIES: string[] = [
  'VE', 'CO', 'MX', 'AR', 'ES', 'CL', 'PE', 'EC', 'BO', 'PY', 'UY',
  'CR', 'CU', 'DO', 'GT', 'HN', 'NI', 'PA', 'SV', 'PR', 'US', 'BR', 'PT',
];

const codes = new Set(COUNTRY_CODES);

export function isCountryCode(value: string): boolean {
  return codes.has(value.toUpperCase());
}

let names: Intl.DisplayNames | null = null;

/** «VE» → «Venezuela». Si el entorno no trae datos, devuelve el código. */
export function countryName(code: string, locale = 'es'): string {
  const upper = code.toUpperCase();
  try {
    names ??= new Intl.DisplayNames([locale], { type: 'region' });
    return names.of(upper) ?? upper;
  } catch {
    return upper;
  }
}

/**
 * «VE» → «🇻🇪».
 *
 * La bandera es el par de letras del código en «indicadores regionales»,
 * que es como se forma cualquier emoji de bandera.
 */
export function countryFlag(code: string): string {
  const upper = code.toUpperCase();
  if (upper.length !== 2) return '';
  return String.fromCodePoint(
    0x1f1e6 + (upper.charCodeAt(0) - 65),
    0x1f1e6 + (upper.charCodeAt(1) - 65),
  );
}

/** La lista para un desplegable: primero los habituales, luego el resto. */
export function countryOptions(locale = 'es'): { code: string; name: string; flag: string }[] {
  const rest = COUNTRY_CODES.filter((code) => !PRIORITY_COUNTRIES.includes(code))
    .map((code) => ({ code, name: countryName(code, locale), flag: countryFlag(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));

  const priority = PRIORITY_COUNTRIES.map((code) => ({
    code,
    name: countryName(code, locale),
    flag: countryFlag(code),
  }));

  return [...priority, ...rest];
}
